import * as React from 'react'
import {ReactElement} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import styled, {css} from 'styled-components'
import {Colors, DispWidthSize, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {isIE} from 'react-device-detect'
import {currentMedia, RenderSwitchByBreakPoint} from '../../../styledComponents/ResponsiveConvenience'
import {NatOwnershipTransfersRepo} from '../ownershipTransfers/repository/NatOwnershipTransfersRepo'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper,
} from '../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import EventEmitterExGuest from '../../../components/shared/pageBaseComponents/EventEmitterExGuest'
import {AsyncCommunicationProps, withAsyncCommunication} from '../../../components/shared/hoc/withAsyncCommunication'
import {getQueryParameter} from '../../common/util/Natutil'
import {Location, NavigateFunction} from 'react-router-dom'

const FOOTER_LINK_URL_MYPAGE: string = '/guest/mypage'
const FOOTER_LINK_URL_CONTACTUS: string = '/guest/contactus'
const FOOTER_LINK_URL_GROUP_LINK: string = 'https://www.haseko.co.jp/hc/company/group.html'
const FOOTER_LINK_URL_PRIVACY: string = 'https://www.haseko.co.jp/hc/privacy.html'
const FOOTER_LINK_URL_POLICY_TERMS: string = '/guest/contents/policy'
const FOOTER_LINK_URL_EXTERNAL_TRANSMISSION: string = '/guest/externalTransmission'

interface Props {
    navigate: NavigateFunction
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    eventEmitter: EventEmitterExGuest
    loginedFlg: boolean
    isNoTransitionFlg?: boolean
    location: Location
}

interface States {
    accessKey: string
    isLoadCompleted:boolean
}

interface ExternalProps {
    eventEmitter: EventEmitterExGuest
}

class NatGuestFooterAreaComponent
    extends React.Component<Props & AsyncCommunicationProps & ExternalProps, States> {
    constructor(props: Props & AsyncCommunicationProps & ExternalProps) {
        super(props)
        this.state = {
            accessKey: '',
            isLoadCompleted: false
        }

        this.onClickHome = this.onClickHome.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const accessKey = await this.getAccessKey()
            this.setState({accessKey: accessKey})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getAccessKey(): Promise<string> {
        const queryAccessKey = getQueryParameter('accessKey', this.props.location.search)

        if (queryAccessKey) {
            return queryAccessKey
        }

        const condominiumCd = NatGuestLoginInfoSessionStorageHelper
            .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_CD) ?? ''
        if (condominiumCd) {
            const accessKeyByLoginUser = await this.props.asyncCallFunction(
                this.props.ownershipTransfersRepo.getAccessKeyByLoginUser.bind(
                    this.props.ownershipTransfersRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            return accessKeyByLoginUser?.details?.accessKey ?? ''
        }

        return ''
    }

    async onClickHome(): Promise<void> {
        const accessKey = this.state.accessKey
        this.props.navigate(`${FOOTER_LINK_URL_MYPAGE}?accessKey=${accessKey}`)
    }

    renderFooterLinkArea(): Nullable<ReactElement> {
        if (this.props.isNoTransitionFlg) {
            return null
        }

        if (!this.state.isLoadCompleted) {
            return null
        }

        const showHomeLink = this.props.loginedFlg

        return <>
            <FooterLinkArea>
                {showHomeLink && <>
                    <FooterBox>
                        <FooterLink onClick={this.onClickHome}>
                            ホーム
                        </FooterLink>
                    </FooterBox>
                    <LinkSeparater/>
                </>}
                <FooterBox>
                    {(isIE) ?
                        <FooterAnchorLink target={'_blank'} onClick={()=>window.open(FOOTER_LINK_URL_CONTACTUS + '?accessKey=' + this.state.accessKey)}>
                            お問い合わせ
                        </FooterAnchorLink> :
                        <FooterAnchorLink href={FOOTER_LINK_URL_CONTACTUS + '?accessKey=' + this.state.accessKey}
                                          target={'_blank'} rel={'opener'}
                        >
                            お問い合わせ
                        </FooterAnchorLink>
                    }
                </FooterBox>
                <LinkSeparater/>
                <FooterBox>
                    <FooterLink onClick={() => {
                        window.open(FOOTER_LINK_URL_GROUP_LINK, '_blank')
                    }}>
                        グループリンク
                    </FooterLink>
                </FooterBox>
                <RenderSwitchByBreakPoint
                    over={<LinkSeparater/>}
                    under={showHomeLink ? <br/> : <LinkSeparater/>}
                />
                <FooterBox>
                    <FooterAnchorLink
                        href={FOOTER_LINK_URL_EXTERNAL_TRANSMISSION + '?accessKey=' + this.state.accessKey} target={'_blank'}
                    >
                        利用者情報の外部送信
                    </FooterAnchorLink>
                </FooterBox>
                <RenderSwitchByBreakPoint
                    over={<LinkSeparater/>}
                    under={showHomeLink ? <LinkSeparater/> : <br/>}
                />
                <FooterBox>
                    <FooterLink onClick={() => {
                        window.open(FOOTER_LINK_URL_PRIVACY, '_blank')
                    }}>
                        プライバシーポリシー
                    </FooterLink>
                </FooterBox>
                <LinkSeparater/>
                <FooterBox>
                    <FooterAnchorLink href={FOOTER_LINK_URL_POLICY_TERMS + '?accessKey=' + this.state.accessKey}
                                      target={'_blank'}
                    >
                        利用規約
                    </FooterAnchorLink>
                </FooterBox>
            </FooterLinkArea>
        </>
    }

    render(): Nullable<ReactElement> {
        return (
            <StyledFooterArea>
                {this.renderFooterLinkArea()}
                <Copywriter>Copyright © HASEKO Corporation. All Rights Reserved.</Copywriter>
            </StyledFooterArea>
        )
    }
}

export default withAsyncCommunication(NatGuestFooterAreaComponent)

const StyledFooterArea: StyledDiv = styled.div`
  background-color: ${Colors.GuestPrimary};
  color: ${Colors.Surface};
  font-weight: ${FontWeights.Normal};
  margin-left: auto;
  margin-right: auto;
  max-width: ${DispWidthSize.MaxWidth};
  box-shadow: 6px 0 5px -5px ${Colors.Shadow}, -6px 0 5px -5px ${Colors.Shadow};
`

const FooterLinkArea: StyledDiv = styled.div`
  ${currentMedia.greaterThan('pc')`
    display: flex;
    align-items: center;
  `}
  ${currentMedia.lessThan('sp')`
    text-align: center;
    padding: 5px 0;
  `}
  border-bottom: 1px solid ${Colors.Surface};
`

const FooterBox: StyledDiv = styled.div`
  ${currentMedia.greaterThan('pc')`
    // 各リンクを同じ幅で横いっぱいにする
    flex: 1 1 0;
    height: 38px;
    margin: 5px;
  `}
  ${currentMedia.lessThan('sp')`
    display: inline-block;
  `}
`

const FooterLinkCss = css`
  font-size: ${FontSize.Small};
  color: ${Colors.Surface};
  white-space: nowrap;
  cursor: pointer;

  ${currentMedia.greaterThan('pc')`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  `}
  ${currentMedia.lessThan('sp')`
    box-sizing: border-box;
    padding: 5px 14px;
  `}
  &:hover {
    background-color: ${Colors.PrimaryComplete};
  }
`

const FooterLink: StyledDiv = styled.div`
  ${FooterLinkCss};
`

const FooterAnchorLink = styled.a`
  ${FooterLinkCss};
  text-decoration: none;
`

const Copywriter: StyledDiv = styled.div`
  text-align: center;
  font-size: ${FontSize.Small};
`

const LinkSeparater = styled.div`
  display: inline-block;
  vertical-align: middle;
  min-width: 1px;
  height: 18px;
  background-color: ${Colors.AccentLight};
`
